import { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import isEqual from "lodash/isEqual";
import {
  getSavedFilters,
  createSavedFilter,
  updateSavedFilter,
  deleteSavedFilter,
} from "../../api";
import useUserStore from "../stores/user";

const TOAST_DURATION_MS = 4000;

function savedFilterErrorMessage(error, fallback = "Something went wrong.") {
  if (typeof error === "string" && error) {
    return error;
  }
  const body = error?.data ?? error;
  if (body?.message) {
    return typeof body.message === "string"
      ? body.message
      : body.message[0] ?? fallback;
  }
  if (body?.error) {
    return typeof body.error === "string" ? body.error : fallback;
  }
  return fallback;
}

/**
 * Saved-filter API + UI state for filter drawers.
 *
 * For Formik-backed drawers, pass getters that read the latest form state via refs * (see merchant FilterDrawer).
 *
 * @param {Object} options
 * @param {string} options.page - Backend page key (e.g. "dashboard", "applications").
 * @param {boolean} options.drawerOpen - When true, list is fetched and selection UI resets.
 * @param {() => object} options.getFilterSnapshot - Current filter payload to persist.
 * @param {(filters: object) => void} options.applyFilterSnapshot - Apply a saved payload to the drawer.
 * @param {(filterId: number) => void} [options.onAfterDelete] - Optional hook after successful delete.
 * @param {(snapshot: object) => boolean} [options.isSnapshotEmpty] - Return true when the snapshot has no
 *   meaningful filters (so saving is blocked). Prevents saving an empty/default preset.
 * @param {() => object} [options.getAppliedSnapshot] - Current **applied** filter payload (e.g. from the
 *   page store after Apply). Used to re-select the matching saved filter when the drawer reopens.
 *
 * Call **commitAppliedSavedFilter** from the drawer when the user clicks Apply (after writing filters to
 * the store). That remembers which saved preset was active so the dropdown stays selected on reopen even
 * when deep equality with the API payload fails.
 */
export function useSavedFilters({
  page,
  drawerOpen,
  getFilterSnapshot,
  applyFilterSnapshot,
  onAfterDelete,
  isSnapshotEmpty,
  getAppliedSnapshot,
}) {
  const isEntitySuperAdmin = useUserStore((state) => state.isEntitySuperAdmin);

  const [savedFilters, setSavedFilters] = useState([]);
  const [selectedSavedFilter, setSelectedSavedFilter] = useState("");
  const [showSaveFilterInput, setShowSaveFilterInput] = useState(false);
  const [newFilterName, setNewFilterName] = useState("");
  const [editFilterName, setEditFilterName] = useState("");
  const [isSavingFilter, setIsSavingFilter] = useState(false);
  const [isUpdatingFilter, setIsUpdatingFilter] = useState(false);
  const [isDeletingFilter, setIsDeletingFilter] = useState(null);

  const getAppliedSnapshotRef = useRef(getAppliedSnapshot);
  getAppliedSnapshotRef.current = getAppliedSnapshot;

  /** Last saved-filter id the user applied (persists across drawer close/open). */
  const lastAppliedSavedFilterIdRef = useRef(null);
  const selectedSavedFilterRef = useRef(selectedSavedFilter);
  selectedSavedFilterRef.current = selectedSavedFilter;

  const commitAppliedSavedFilter = useCallback(() => {
    const raw = selectedSavedFilterRef.current;
    if (raw === "" || raw === null || raw === undefined) {
      lastAppliedSavedFilterIdRef.current = null;
      return;
    }
    const parsed = parseInt(raw, 10);
    lastAppliedSavedFilterIdRef.current = Number.isNaN(parsed) ? raw : parsed;
  }, []);

  const fetchSavedFilters = useCallback(async () => {
    try {
      const response = await getSavedFilters(page);
      if (response.success && Array.isArray(response.data)) {
        setSavedFilters(response.data);
        return;
      }
      toast.error(
        response.message || "Could not load saved filters.",
        { duration: TOAST_DURATION_MS },
      );
      setSavedFilters([]);
    } catch (error) {
      console.error("Failed to fetch saved filters:", error);
      toast.error(
        savedFilterErrorMessage(error, "Could not load saved filters."),
        { duration: TOAST_DURATION_MS },
      );
      setSavedFilters([]);
    }
  }, [page]);

  useEffect(() => {
    if (!drawerOpen || !isEntitySuperAdmin) {
      return;
    }

    setShowSaveFilterInput(false);
    setNewFilterName("");

    let cancelled = false;

    (async () => {
      try {
        const response = await getSavedFilters(page);
        if (cancelled) {
          return;
        }

        if (response.success && Array.isArray(response.data)) {
          setSavedFilters(response.data);

          const remembered = lastAppliedSavedFilterIdRef.current;
          if (remembered != null) {
            const byId = response.data.find(
              (f) =>
                f.id === remembered || String(f.id) === String(remembered),
            );
            if (byId) {
              setSelectedSavedFilter(String(byId.id));
              setEditFilterName(byId.name);
              return;
            }
            lastAppliedSavedFilterIdRef.current = null;
          }

          const getApplied = getAppliedSnapshotRef.current;
          if (typeof getApplied === "function") {
            const applied = getApplied();
            const match = response.data.find((f) =>
              isEqual(f.filters ?? {}, applied),
            );
            if (match) {
              setSelectedSavedFilter(String(match.id));
              setEditFilterName(match.name);
              lastAppliedSavedFilterIdRef.current = match.id;
            } else {
              setSelectedSavedFilter("");
              setEditFilterName("");
            }
          } else {
            setSelectedSavedFilter("");
            setEditFilterName("");
          }
          return;
        }

        toast.error(
          response.message || "Could not load saved filters.",
          { duration: TOAST_DURATION_MS },
        );
        setSavedFilters([]);
        setSelectedSavedFilter("");
        setEditFilterName("");
      } catch (error) {
        if (cancelled) {
          return;
        }
        console.error("Failed to fetch saved filters:", error);
        toast.error(
          savedFilterErrorMessage(error, "Could not load saved filters."),
          { duration: TOAST_DURATION_MS },
        );
        setSavedFilters([]);
        setSelectedSavedFilter("");
        setEditFilterName("");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [drawerOpen, page, isEntitySuperAdmin]);

  const openSaveFilterInput = useCallback(() => {
    setShowSaveFilterInput(true);
  }, []);

  const cancelSaveFilterInput = useCallback(() => {
    setShowSaveFilterInput(false);
  }, []);

  const handleSavedFilterSelect = (value) => {
    const filterId = value?.value ?? value ?? "";
    setSelectedSavedFilter(filterId);

    if (!filterId) {
      setEditFilterName("");
      lastAppliedSavedFilterIdRef.current = null;
      return;
    }

    const savedFilter = savedFilters.find(
      (f) => f.id === parseInt(filterId, 10),
    );
    if (savedFilter) {
      setEditFilterName(savedFilter.name);
      if (savedFilter.filters) {
        applyFilterSnapshot(savedFilter.filters);
      }
    }
  };

  const handleSaveFilter = async () => {
    if (!newFilterName.trim()) {
      return;
    }

    const snapshot = getFilterSnapshot();
    if (typeof isSnapshotEmpty === "function" && isSnapshotEmpty(snapshot)) {
      toast.error(
        "Choose at least one filter before saving.",
        { duration: TOAST_DURATION_MS },
      );
      return;
    }

    setIsSavingFilter(true);
    try {
      const response = await createSavedFilter({
        name: newFilterName.trim(),
        page,
        filters: snapshot,
      });
      if (response.success) {
        toast.success(
          response.message || "Filter saved.",
          { duration: TOAST_DURATION_MS },
        );
        setShowSaveFilterInput(false);
        setNewFilterName("");
        await fetchSavedFilters();
      } else {
        toast.error(
          response.message || "Could not save filter.",
          { duration: TOAST_DURATION_MS },
        );
      }
    } catch (error) {
      console.error("Failed to save filter:", error);
      toast.error(
        savedFilterErrorMessage(error, "Could not save filter."),
        { duration: TOAST_DURATION_MS },
      );
    } finally {
      setIsSavingFilter(false);
    }
  };

  const handleUpdateSavedFilter = async () => {
    if (!selectedSavedFilter || !editFilterName.trim()) {
      return;
    }

    const savedFilter = savedFilters.find(
      (f) => f.id === parseInt(selectedSavedFilter, 10),
    );
    if (!savedFilter) {
      return;
    }

    const snapshot = getFilterSnapshot();
    const storedFilters = savedFilter.filters ?? {};
    if (
      editFilterName.trim() === savedFilter.name &&
      isEqual(snapshot, storedFilters)
    ) {
      toast.error("No changes to save.", { duration: TOAST_DURATION_MS });
      return;
    }

    setIsUpdatingFilter(true);
    try {
      const response = await updateSavedFilter(
        parseInt(selectedSavedFilter, 10),
        {
          name: editFilterName.trim(),
          filters: snapshot,
        },
      );
      if (response.success) {
        toast.success(
          response.message || "Filter updated.",
          { duration: TOAST_DURATION_MS },
        );
        await fetchSavedFilters();
      } else {
        toast.error(
          response.message || "Could not update filter.",
          { duration: TOAST_DURATION_MS },
        );
      }
    } catch (error) {
      console.error("Failed to update saved filter:", error);
      toast.error(
        savedFilterErrorMessage(error, "Could not update filter."),
        { duration: TOAST_DURATION_MS },
      );
    } finally {
      setIsUpdatingFilter(false);
    }
  };

  const handleDeleteSavedFilter = async (e, filterId) => {
    e.stopPropagation();
    setIsDeletingFilter(filterId);
    try {
      const response = await deleteSavedFilter(filterId);
      if (response.success) {
        toast.success(
          response.message || "Filter deleted.",
          { duration: TOAST_DURATION_MS },
        );
        if (parseInt(selectedSavedFilter, 10) === filterId) {
          setSelectedSavedFilter("");
          setEditFilterName("");
        }
        if (
          lastAppliedSavedFilterIdRef.current === filterId ||
          String(lastAppliedSavedFilterIdRef.current) === String(filterId)
        ) {
          lastAppliedSavedFilterIdRef.current = null;
        }
        await fetchSavedFilters();
        onAfterDelete?.(filterId);
      } else {
        toast.error(
          response.message || "Could not delete filter.",
          { duration: TOAST_DURATION_MS },
        );
      }
    } catch (error) {
      console.error("Failed to delete saved filter:", error);
      toast.error(
        savedFilterErrorMessage(error, "Could not delete filter."),
        { duration: TOAST_DURATION_MS },
      );
    } finally {
      setIsDeletingFilter(null);
    }
  };

  const savedFilterOptions = savedFilters.map((f) => ({
    label: f.name,
    value: f.id,
  }));

  return {
    savedFilterOptions,
    selectedSavedFilter,
    showSaveFilterInput,
    openSaveFilterInput,
    cancelSaveFilterInput,
    newFilterName,
    setNewFilterName,
    editFilterName,
    setEditFilterName,
    isSavingFilter,
    isUpdatingFilter,
    isDeletingFilter,
    handleSavedFilterSelect,
    handleSaveFilter,
    handleUpdateSavedFilter,
    handleDeleteSavedFilter,
    commitAppliedSavedFilter,
  };
}
